import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import numbro from 'numbro';
import { shallow } from 'zustand/shallow';

import { LoadingSpinner } from '../../components/Molecules';
import { CantBecomeRolePopup } from '../../components/Popups';
import { LeaveProductPopup } from '../../components/Popups/LeaveProductPopup';
import {
  // SmartParentsProgramTemplate,
  WebStoreProductDetailTemplate,
} from '../../components/Templates';
import { DEFAULT_PARTNERID } from '../../constants/appInfo';
import { RegionsCode } from '../../constants/region';
import { AppToastErrorOption } from '../../constants/toast';
import { usePopup, useShareData } from '../../context';
import useDoWhile, { DoWhileFn } from '../../hooks/useDoWhile';
import useFetchList, { FetchListFn } from '../../hooks/useFetchList';
import useLocale from '../../hooks/useLocale';
import useRedirectHome from '../../hooks/useRedirectHome';
import { AttachmentImage } from '../../models/Base';
import {
  ReferralSystem,
  RewardOfferType,
  RewardRedemptionState,
  RewardReferralRole,
} from '../../models/RewardStore';
import { SpaceMembershipTierSubscriptionListType } from '../../models/SpaceStore';
import { Profile, ResetPasswordResult } from '../../models/UserStore';
import {
  useRewardStore,
  useRewardStoreListField,
} from '../../stores/RewardStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useSpaceStore, useSpaceStoreListField } from '../../stores/SpaceStore';
import { useUserStore } from '../../stores/UserStore';
import { arrayEqual } from '../../utils/array';
import { getLocalCurrencyByRegion } from '../../utils/region';

type State = {
  loadingTotal: number;
  finishFetchOffer: boolean;
  referralCode: string | undefined;
  lastReferralCode: string | undefined;
  referredByProfile: Profile | null | undefined;
  isValid: boolean;
  isLoading: boolean;
  rewardRedemptionId: string | undefined;
  userRewardReferralCode: string | undefined;
  hasCancelled: boolean;
  isNeedFetchAgain: boolean;
  hasLearnMoreButton: boolean;
  hasShareButton: boolean;
  shareButtonDisabled: boolean;
  hasPurchaseButton: boolean;
  purchasedButtonDisabled: boolean;
  hasSelectVersionButton: boolean;
  enterPurchasedClubButton: string | null | undefined; // classroomId -> club page, null -> enter purchased page, undefined -> no club
  hasApplyButton: boolean;
  isExpired: boolean;
  expiredAt: Date | undefined;
};

const {
  fetchOffer,
  fetchRewardReferral,
  createRedemption,
  fetchRedemption,
  cancelRedemption,
  referralCodeValidation,
  createRewardReferral,
} = useRewardStore.getState();

const WebStoreProductDetailScreen = () => {
  const navigate = useNavigate();
  const l = useLocale();
  const { t } = useTranslation();
  const location = useLocation();
  const { sharedData, setSharedData } = useShareData();
  const { showPopup, hidePopup } = usePopup();

  const { rewardOfferId } = useParams();
  const activeRegion = useSessionStore((store) => store.activeRegion);
  const isLoggedIn = useSessionStore((store) => store.isLoggedIn);

  const userId = useSessionStore((store) => store.activeUserId);

  const userProfile = useUserStore((store) => store.getProfile(userId ?? ''));
  const userAccount = useUserStore((store) => store.getAccount(userId ?? ''));
  const isTrialUser = userProfile?.isTrialUser ?? false;

  const query = new URLSearchParams(location.search);

  const redemptionId = query.get('rewardRedemptionId');
  const referralCode = query.get('referralCode');

  const currency = useMemo(() => {
    return userProfile?.localCurrency ?? getLocalCurrencyByRegion(activeRegion);
  }, [activeRegion, userProfile?.localCurrency]);

  const [state, setState] = useState<State>({
    loadingTotal: 0,
    finishFetchOffer: false,
    referralCode: undefined,
    lastReferralCode: undefined,
    referredByProfile: undefined,
    isValid: false,
    isLoading: false,
    rewardRedemptionId: undefined,
    userRewardReferralCode: undefined,
    hasCancelled: false,
    isNeedFetchAgain: false,
    hasLearnMoreButton: false,
    hasShareButton: false,
    shareButtonDisabled: false,
    hasPurchaseButton: false,
    purchasedButtonDisabled: false,
    hasSelectVersionButton: false,
    enterPurchasedClubButton: undefined,
    hasApplyButton: false,
    isExpired: false,
    expiredAt: undefined,
  });

  useEffect(() => {
    setSharedData({
      pathName: location.pathname,
      searchReferralCode: referralCode ? referralCode : '',
    });
  }, [location.pathname, referralCode, setSharedData]);

  const getReferralCode = useCallback(async () => {
    setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
    const referralCodeData = await useRewardStore
      .getState()
      .getRewardReferralCode(ReferralSystem.Joyajoy);
    setState((s) => ({ ...s, loadingTotal: s.loadingTotal - 1 }));
    if (referralCodeData) {
      setState((s) => ({
        ...s,
        userRewardReferralCode: referralCodeData.referralCode,
      }));
    }
  }, []);

  useEffect(() => {
    if (userId) {
      getReferralCode().catch(console.error);
    }
  }, [getReferralCode, userId]);

  useEffect(() => {
    if (userId) {
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
      fetchRewardReferral({ referralSystem: ReferralSystem.Joyajoy })
        .catch(console.error)
        .finally(() => {
          setState((s) => ({ ...s, loadingTotal: s.loadingTotal - 1 }));
        });
    }
  }, [userId]);

  const rewardReferral = useRewardStore(
    (store) => store.getRewardReferral(`${ReferralSystem.Joyajoy}_${userId}`),
    shallow,
  );

  useEffect(() => {
    const code =
      rewardReferral?.rewardReferralCode?.referralCode ?? referralCode;
    if (code) {
      setState((s) => ({
        ...s,
        referralCode: code,
      }));
    }
  }, [referralCode, rewardReferral?.rewardReferralCode?.referralCode]);

  const rewardOfferStore = 'system';

  useRewardStoreListField('offers', `${rewardOfferStore}_${rewardOfferId}`);

  useEffect(() => {
    if (rewardOfferId) {
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
      fetchOffer(rewardOfferStore, rewardOfferId)
        .catch(console.error)
        .finally(() => {
          setState((s) => ({
            ...s,
            loadingTotal: s.loadingTotal - 1,
            finishFetchOffer: true,
          }));
        });
    }
  }, [rewardOfferId]);

  const rewardOffer = useRewardStore(
    (store) => store.getOffer(rewardOfferId ?? ''),
    shallow,
  );

  useRedirectHome(state.finishFetchOffer && !rewardOffer);

  const checkReferralCode = useCallback(async () => {
    if (state.referralCode === state.userRewardReferralCode) {
      setState((s) => ({
        ...s,
        isValid: false,
        // errorMessage: t('Invalid Code. This is your referral code.'),
      }));
      return;
    }

    if (state.referralCode === state.lastReferralCode) {
      return;
    }

    if (state.referralCode) {
      const res = await referralCodeValidation(
        { referralSystem: ReferralSystem.Joyajoy },
        { referralCode: state.referralCode },
      );
      setState((s) => ({
        ...s,
        lastReferralCode: state.referralCode,
      }));
      if (res === ResetPasswordResult.Success) {
        setState((s) => ({
          ...s,
          isValid: true,
        }));
        setSharedData({
          ...sharedData,
          referralCode:
            rewardOffer?.reward.type === RewardOfferType.ReferralRole
              ? ''
              : state.referralCode,
        });
      } else if (res === ResetPasswordResult.Error) {
        setState((s) => ({
          ...s,
          isValid: false,
        }));
        setSharedData({
          ...sharedData,
          referralCode: '',
          searchReferralCode: '',
        });
      } else {
        setState((s) => ({
          ...s,
          referredByProfile: res,
          isValid: true,
        }));
        setSharedData({
          ...sharedData,
          referralCode:
            rewardOffer?.reward.type === RewardOfferType.ReferralRole
              ? ''
              : state.referralCode,
        });
      }
    } else {
      setState((s) => ({ ...s, isValid: false }));
    }
  }, [
    state.referralCode,
    state.userRewardReferralCode,
    state.lastReferralCode,
    setSharedData,
    sharedData,
    rewardOffer?.reward.type,
  ]);

  useEffect(() => {
    if (state.referralCode) {
      checkReferralCode().catch(() => {});
    }
  }, [checkReferralCode, state.referralCode]);

  useEffect(() => {
    if (state.isValid && referralCode && userId) {
      if (
        !rewardReferral?.referredBy &&
        rewardOffer?.reward.type !== RewardOfferType.ReferralRole
      ) {
        createRewardReferral(
          { referralSystem: ReferralSystem.Joyajoy },
          { referralCode: referralCode },
        ).catch(console.error);
      }
    }
  }, [
    rewardReferral?.referredBy,
    state.isValid,
    referralCode,
    rewardOffer?.reward.type,
    userId,
  ]);

  const rewardRedemptionsFieldName = `${rewardOfferStore}_${rewardOfferId}`;
  const rewardRedemptionsField = useRewardStoreListField(
    'redemptions',
    rewardRedemptionsFieldName,
  );

  const fetchRewardRedemptions = useCallback<FetchListFn>(
    (cursor) =>
      useRewardStore
        .getState()
        .fetchRedemptionsByOfferId(
          rewardOfferStore,
          rewardOfferId ?? '',
          cursor,
        ),
    [rewardOfferId],
  );

  const { fetchAll: fetchAllRewardRedemptions } = useFetchList(
    rewardRedemptionsField,
    fetchRewardRedemptions,
  );

  useEffect(() => {
    if ((userId && rewardOfferId) || state.isNeedFetchAgain) {
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
      fetchAllRewardRedemptions()
        .catch(console.error)
        .finally(() => {
          setState((s) => ({
            ...s,
            loadingTotal: s.loadingTotal - 1,
            isNeedFetchAgain: false,
          }));
        });
    }
  }, [
    fetchAllRewardRedemptions,
    rewardOfferId,
    state.isNeedFetchAgain,
    userId,
  ]);

  const rewardRedemptions = useRewardStore((rewardState) => {
    return rewardState.getRedemptions(rewardRedemptionsFieldName);
  }, arrayEqual(shallow));

  const completedRedemption = useMemo(() => {
    return rewardRedemptions.find(
      // TODO: may need to check expiered if yearly
      (item) => item.state === RewardRedemptionState.Fulfilled,
    );
  }, [rewardRedemptions]);

  const payingRedemption = useMemo(() => {
    return rewardRedemptions.find(
      (item) => item.state === RewardRedemptionState.Paying,
    );
  }, [rewardRedemptions]);

  useEffect(() => {
    if (
      (redemptionId !== null ||
        payingRedemption?.rewardRedemptionId !== undefined) &&
      !state.hasCancelled
    ) {
      const idToCancel: string =
        redemptionId || payingRedemption?.rewardRedemptionId || '';

      cancelRedemption(rewardOfferStore, idToCancel)
        .catch(console.error)
        .finally(() => {
          setState((s) => ({ ...s, hasCancelled: true }));
        });
    }
  }, [payingRedemption?.rewardRedemptionId, redemptionId, state.hasCancelled]);

  const loadRedemption = useDoWhile(
    useCallback<DoWhileFn>(
      async (signal) => {
        if (state.rewardRedemptionId !== undefined) {
          const res = await fetchRedemption(
            rewardOfferStore,
            state.rewardRedemptionId,
            { signal },
          );
          if (res) {
            switch (res.state) {
              case RewardRedemptionState.Paying:
                {
                  const url = res.payment?.url;
                  if (url !== undefined) {
                    setState((s) => ({ ...s, isLoading: false }));
                    window.location.href = url;
                  }
                }
                return true;
              case RewardRedemptionState.Fulfilled:
                return true;
              case RewardRedemptionState.Failed:
                return false;
            }
          }
        }
        await new Promise((resolve) => setTimeout(resolve, 3000));
        return false;
      },
      [state.rewardRedemptionId],
    ),
  );

  useEffect(() => {
    if (state.rewardRedemptionId !== undefined) {
      loadRedemption().catch(() => {});
    }
  }, [loadRedemption, state.rewardRedemptionId]);

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        const url = new URL(window.location.href);
        if (url.search) {
          const queryParams = new URLSearchParams(window.location.search);
          queryParams.delete('rewardRedemptionId');

          url.search = queryParams.toString();
          window.history.replaceState(null, '', url.toString());
          window.location.reload(); // Reload the page when navigating back
        } else {
          window.location.reload(); // Reload the page when navigating back
        }
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  const spaceMembershipTierSubscriptionFieldName =
    SpaceMembershipTierSubscriptionListType.ActiveUser;
  const spaceMembershipTierSubscriptionField = useSpaceStoreListField(
    'spaceMembershipTierSubscriptions',
    spaceMembershipTierSubscriptionFieldName,
  );
  const fetchTierSubscriptions = useCallback<FetchListFn>(
    (cursor) => useSpaceStore.getState().fetchTierSubscriptions(cursor),
    [],
  );
  const { fetchAll: fetchAllTierSubscriptions } = useFetchList(
    spaceMembershipTierSubscriptionField,
    fetchTierSubscriptions,
  );

  useEffect(() => {
    if (userId && rewardOfferId) {
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
      fetchAllTierSubscriptions()
        .catch(console.error)
        .finally(() => {
          setState((s) => ({
            ...s,
            loadingTotal: s.loadingTotal - 1,
          }));
        });
    }
  }, [fetchAllTierSubscriptions, rewardOfferId, userId]);

  const spaceMembershipTierSubscriptions = useSpaceStore((store) => {
    return store.getTierSubscriptions(spaceMembershipTierSubscriptionFieldName);
  }, arrayEqual(shallow));

  const activatedTierSubscriptions = useMemo(() => {
    return spaceMembershipTierSubscriptions.filter(
      (subscriptions) => subscriptions.isActivated,
    );
  }, [spaceMembershipTierSubscriptions]);

  const expiredTierSubscriptions = useMemo(() => {
    return spaceMembershipTierSubscriptions.filter(
      (subscriptions) => subscriptions.isExpired,
    );
  }, [spaceMembershipTierSubscriptions]);

  const i18nRewardOfferInfo = l(rewardOffer?.i18nRewardOfferInfo);

  const carousel = useMemo(() => {
    return (
      i18nRewardOfferInfo?.carousel
        .map((item) => item.image)
        .filter((image): image is AttachmentImage => image !== undefined)
        .map((image) => ({
          resource: { uri: image.publicUrls?.large ?? '' },
        })) ?? []
    );
  }, [i18nRewardOfferInfo?.carousel]);

  const getPrices = useCallback(
    (
      isReferralPrice: boolean,
      isReferralReward: boolean,
    ): string | undefined => {
      const priceList = rewardOffer?.price;
      const isRoleRewardType =
        rewardOffer?.reward.type === RewardOfferType.ReferralRole;
      const isCreationTokeOffer =
        rewardOffer?.reward.type === RewardOfferType.Token;
      const isValid = state.isValid;

      const rewardRatio = {
        distributor: 0.25,
        normal: 0.1,
      };

      let c = currency;
      let priceNumber =
        isReferralPrice && isValid && !isRoleRewardType && !isCreationTokeOffer
          ? priceList?.[`referral${currency}`]
          : priceList?.[`${currency}`];
      if (priceNumber === undefined) {
        c = 'HKD';
        priceNumber =
          (isReferralPrice &&
          isValid &&
          !isRoleRewardType &&
          !isCreationTokeOffer
            ? priceList?.[`referralHKD`]
            : priceList?.[`HKD`]) ?? 0;
      }

      if (isReferralReward) {
        if (isRoleRewardType) {
          // hardcode the value now
          priceNumber = rewardOfferId === DEFAULT_PARTNERID ? 500000 : 300000;
          priceNumber =
            priceNumber *
            (rewardReferral?.role === RewardReferralRole.Partner ? 1.1 : 1);
        } else {
          priceNumber =
            priceNumber *
            (rewardReferral?.role === RewardReferralRole.Distributor
              ? rewardRatio.distributor
              : rewardRatio.normal);
        }
      }

      if (!isValid && (!isReferralPrice || isReferralReward)) {
        return undefined;
      }

      if ((isRoleRewardType || isCreationTokeOffer) && !isReferralPrice) {
        return undefined;
      }

      return `${isReferralReward && isRoleRewardType ? 'HKD' : c}${numbro(
        priceNumber / 100,
      ).format({
        thousandSeparated: true,
        mantissa: 2,
      })}`;
    },
    [
      rewardOffer?.price,
      rewardOffer?.reward.type,
      state.isValid,
      currency,
      rewardOfferId,
      rewardReferral?.role,
    ],
  );

  useEffect(() => {
    if (
      (rewardReferral?.isPartnerOrDistributorRole ||
        (userId &&
          rewardOffer?.reward.type !== RewardOfferType.ReferralRole)) &&
      rewardOffer?.reward.type !== RewardOfferType.Token
    ) {
      setState((s) => ({ ...s, hasShareButton: true }));
    } else {
      setState((s) => ({ ...s, hasShareButton: false }));
    }
  }, [
    rewardOffer?.reward.type,
    rewardReferral?.isPartnerOrDistributorRole,
    userId,
  ]);

  useEffect(() => {
    if (
      rewardReferral?.role === RewardReferralRole.Partner &&
      rewardOffer?.reward.type !== RewardOfferType.ReferralRole
    ) {
      setState((s) => ({ ...s, shareButtonDisabled: true }));
    } else {
      setState((s) => ({ ...s, shareButtonDisabled: false }));
    }
  }, [rewardOffer?.reward.type, rewardReferral?.role]);

  // const onClickShare = useCallback(() => {
  //   if (state.shareButtonDisabled) {
  //     return;
  //   }

  //   const pageUrl = window.location.href;

  //   const updatedUrl = new URL(pageUrl);
  //   updatedUrl.pathname = `/store/${rewardOfferId}`;

  //   const queryParams = new URLSearchParams();
  //   queryParams.set('referralCode', state.userRewardReferralCode ?? '');
  //   updatedUrl.search = queryParams.toString();

  //   if (navigator.share) {
  //     navigator.share({ url: updatedUrl.toString() }).catch((err) => {
  //       console.error('Failed to share the URL: ', err);
  //     });
  //   } else if (navigator.clipboard) {
  //     navigator.clipboard
  //       .writeText(updatedUrl.toString())
  //       .then(() => {
  //         // setCopied(true);
  //         // setTimeout(() => setCopied(false), 2000); // reset after 2s
  //         toast(t('Link is copied!'), {
  //           ...AppToastInfoOption,
  //           id: 'clipboard',
  //         });
  //       })
  //       .catch((err) => {
  //         console.error('Failed to copy the URL: ', err);
  //       });
  //   } else {
  //     toast(updatedUrl.toString(), AppToastInfoOption);
  //   }
  // }, [
  //   rewardOfferId,
  //   state.shareButtonDisabled,
  //   state.userRewardReferralCode,
  //   t,
  // ]);

  useEffect(() => {
    if (
      rewardReferral?.isPartnerOrDistributorRole ||
      rewardOffer?.reward.type === RewardOfferType.ReferralRole
    ) {
      setState((s) => ({ ...s, hasPurchaseButton: false }));
    } else {
      setState((s) => ({ ...s, hasPurchaseButton: true }));
    }
  }, [rewardOffer?.reward.type, rewardReferral?.isPartnerOrDistributorRole]);

  useEffect(() => {
    if (
      completedRedemption !== undefined &&
      rewardOffer?.reward.type !== RewardOfferType.Token
    ) {
      setState((s) => ({ ...s, purchasedButtonDisabled: true }));
    } else {
      setState((s) => ({ ...s, purchasedButtonDisabled: false }));
    }
  }, [completedRedemption, rewardOffer?.reward.type]);

  useEffect(() => {
    if (
      !rewardReferral?.isPartnerOrDistributorRole &&
      rewardOffer?.reward.type === RewardOfferType.ReferralRole
    ) {
      setState((s) => ({ ...s, hasApplyButton: true }));
    } else {
      setState((s) => ({ ...s, hasApplyButton: false }));
    }
  }, [rewardOffer?.reward.type, rewardReferral?.isPartnerOrDistributorRole]);

  const onClickPurchase = useCallback(() => {
    if (!isLoggedIn()) {
      navigate('/start');
      return;
    }

    if (isTrialUser) {
      navigate('/register');
      return;
    }

    if (!rewardOfferId) {
      toast(t('Product not found'), AppToastErrorOption);
      return;
    }

    if (state.purchasedButtonDisabled) {
      return;
    }

    // if (state.referredByProfile) {
    //   const confirmed = await new Promise((resolve) => {
    //     showPopup(
    //       <ConfirmReferralPopup
    //         onPrimary={() => {
    //           hidePopup();
    //           resolve(true);
    //         }}
    //         onCancel={() => {
    //           hidePopup();
    //           resolve(false);
    //         }}
    //         profile={state.referredByProfile!}
    //       />,
    //     );
    //   });

    //   if (!confirmed) return;
    // }

    if (
      rewardOffer?.reward.type === RewardOfferType.ReferralRole &&
      rewardReferral
    ) {
      showPopup(
        <CantBecomeRolePopup
          rewardReferralRole={rewardOffer.reward.role}
          onPrimary={hidePopup}
        />,
      );
      return;
    }

    setState((s) => ({ ...s, isLoading: true }));

    let variant: Record<string, string> | undefined = undefined;
    if (
      rewardOffer?.reward.type === RewardOfferType.ClassroomMembershipTierSet
    ) {
      const item = rewardOffer.reward.items.find((_item) => _item);
      if (item) {
        variant = item.variant;
      }
    } else if (rewardOffer?.reward.type === RewardOfferType.WebPlan) {
      const item = rewardOffer.reward.items.find((_item) => _item);
      if (item) {
        variant = item.variant;
      }
    }

    createRedemption(rewardOfferStore, {
      rewardOfferId: rewardOfferId,
      quantity: 1,
      paymentGateway: activeRegion === RegionsCode.CN ? 'stripe' : 'paypal',
      referralCode: state.isValid ? state.referralCode : undefined,
      variant: variant,
      currency: getPrices(false, false)?.slice(0, 3),
    })
      .then((res) => {
        if (res) {
          setState((s) => ({
            ...s,
            rewardRedemptionId: res.rewardRedemptionId,
          }));
        } else {
          setState((s) => ({ ...s, isLoading: false }));
          toast(
            t('Failed to create order. Please refresh the page and try again.'),
            AppToastErrorOption,
          );
          setState((s) => ({ ...s, isNeedFetchAgain: true }));
        }
      })
      .catch(() => {
        setState((s) => ({ ...s, isLoading: false }));
        toast(
          t('Failed to create order. Please refresh the page and try again.'),
          AppToastErrorOption,
        );
        setState((s) => ({ ...s, isNeedFetchAgain: true }));
      });
  }, [
    activeRegion,
    getPrices,
    hidePopup,
    isLoggedIn,
    isTrialUser,
    navigate,
    rewardOffer?.reward,
    rewardOfferId,
    rewardReferral,
    showPopup,
    state.isValid,
    state.purchasedButtonDisabled,
    state.referralCode,
    t,
  ]);

  useEffect(() => {
    if (
      rewardOffer &&
      rewardOffer.reward.type === RewardOfferType.ClassroomMembershipTierSet
    ) {
      const variants = rewardOffer.reward.items;

      if (variants.length > 1) {
        setState((s) => ({ ...s, hasSelectVersionButton: true }));
      } else {
        setState((s) => ({ ...s, hasSelectVersionButton: false }));
      }
    } else if (
      rewardOffer &&
      rewardOffer.reward.type === RewardOfferType.WebPlan
    ) {
      if (userAccount?.activeWebPlanTier) {
        setState((s) => ({ ...s, hasSelectVersionButton: false }));
      } else {
        setState((s) => ({ ...s, hasSelectVersionButton: true }));
      }
    } else {
      setState((s) => ({ ...s, hasSelectVersionButton: false }));
    }
  }, [rewardOffer, userAccount?.activeWebPlanTier]);

  const onClickSelectVersion = useCallback(() => {
    navigate(`/store/${rewardOfferId}/select-version`);
  }, [navigate, rewardOfferId]);

  useEffect(() => {
    if (
      rewardOffer &&
      rewardOffer.reward.type === RewardOfferType.ClassroomMembershipTierSet
    ) {
      const variants = rewardOffer.reward.items;

      if (variants.length > 1) {
        setState((s) => ({ ...s, enterPurchasedClubButton: null }));
      } else {
        const classroomId = variants[0]?.classroomId;
        setState((s) => ({ ...s, enterPurchasedClubButton: classroomId }));
      }
    } else {
      setState((s) => ({ ...s, enterPurchasedClubButton: undefined }));
    }
  }, [rewardOffer]);

  const onClickEnterPurchasedClub = useCallback(() => {
    if (state.enterPurchasedClubButton) {
      navigate(`/club/${state.enterPurchasedClubButton}`);
    } else {
      navigate(`/store/${rewardOfferId}/enter-purchased-club`);
    }
  }, [navigate, rewardOfferId, state.enterPurchasedClubButton]);

  useEffect(() => {
    if (rewardOffer) {
      if (
        rewardOffer.reward.type === RewardOfferType.ClassroomMembershipTierSet
      ) {
        const variants = rewardOffer.reward.items;

        const activatedSubscription = activatedTierSubscriptions.find(
          (subscription) =>
            variants.some(
              (variant) =>
                subscription.tierId === variant.tierId &&
                subscription.classroomId === variant.classroomId,
            ),
        );
        if (activatedSubscription) {
          setState((s) => ({
            ...s,
            isExpired: false,
            expiredAt:
              variants.length > 1 ? undefined : activatedSubscription.expiredAt,
          }));
        } else {
          const expiredSubscription = expiredTierSubscriptions.find(
            (subscription) =>
              variants.some(
                (variant) =>
                  subscription.tierId === variant.tierId &&
                  subscription.classroomId === variant.classroomId,
              ),
          );
          if (expiredSubscription) {
            setState((s) => ({
              ...s,
              isExpired: true,
              expiredAt: expiredSubscription.expiredAt,
            }));
          } else {
            setState((s) => ({
              ...s,
              isExpired: false,
              expiredAt: undefined,
            }));
          }
        }
      } else if (rewardOffer.reward.type === RewardOfferType.ReferralRole) {
        if (rewardReferral && rewardReferral.role === rewardOffer.reward.role) {
          setState((s) => ({
            ...s,
            isExpired: rewardReferral.isExpired,
            expiredAt: rewardReferral.expiredAt,
          }));
        } else {
          setState((s) => ({ ...s, isExpired: false, expiredAt: undefined }));
        }
      } else if (rewardOffer.reward.type === RewardOfferType.WebPlan) {
        setState((s) => ({
          ...s,
          isExpired: !userAccount?.activeWebPlanTier,
          expiredAt: userAccount?.webPlanSubscription?.expiredAt,
        }));
      }
    } else {
      setState((s) => ({ ...s, isExpired: false, expiredAt: undefined }));
    }
  }, [
    activatedTierSubscriptions,
    expiredTierSubscriptions,
    rewardOffer,
    rewardReferral,
    userAccount?.activeWebPlanTier,
    userAccount?.webPlanSubscription?.expiredAt,
  ]);

  useEffect(() => {
    if (
      rewardOffer?.reward.type === RewardOfferType.ClassroomMembershipTierSet ||
      rewardOffer?.reward.type === RewardOfferType.WebPlan
    ) {
      const link = rewardOffer.reward.link;
      if (link) {
        setState((s) => ({ ...s, hasLearnMoreButton: true }));
      }
    }
  }, [rewardOffer]);

  const onClickLearnMore = useCallback(() => {
    if (
      rewardOffer?.reward.type === RewardOfferType.ClassroomMembershipTierSet ||
      rewardOffer?.reward.type === RewardOfferType.WebPlan
    ) {
      const link = rewardOffer.reward.link;
      if (link) {
        window.location.href = link;
      }
    }
  }, [rewardOffer]);

  const additionalDetails = undefined; // no additional details now

  const onBack = useCallback(() => {
    if (!userId && referralCode) {
      showPopup(
        <LeaveProductPopup
          onCancel={() => {
            hidePopup();
            // setTimeout(() => {
            //   // not sure why but need this push state to work
            //   // const currentState = window.history.state;
            //   window.history.pushState(
            //     window.history.state,
            //     '',
            //     window.location.href,
            //   );
            // }, 0);
          }}
          onPrimary={() => {
            navigate(-1);
            hidePopup();
          }}
        />,
      );
    } else {
      navigate(-1);
    }
  }, [hidePopup, navigate, referralCode, showPopup, userId]);

  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      // console.log('entered');
      e.preventDefault();
      onBack();
    };
    // console.log('entered2');
    // const currentUrl = new URL(window.location.href);
    // window.history.pushState(window.history.state, '', currentUrl);
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [onBack]);

  return (
    <>
      {state.loadingTotal || state.isLoading ? (
        <LoadingSpinner loading={!!state.loadingTotal || state.isLoading} />
      ) : null}
      <WebStoreProductDetailTemplate
        profile={userProfile}
        rewardReferralRole={rewardReferral?.role}
        userRewardReferralCode={
          rewardReferral?.isPartnerOrDistributorRole
            ? state.userRewardReferralCode
            : undefined
        }
        carousel={carousel}
        productName={i18nRewardOfferInfo?.name}
        price={getPrices(true, false)}
        originalPrice={getPrices(false, false)}
        referralReward={getPrices(true, true)}
        description={i18nRewardOfferInfo?.description}
        shareButtonDisabled={state.shareButtonDisabled}
        purchaseButtonDisabled={
          state.purchasedButtonDisabled && !state.hasSelectVersionButton
        }
        purchaseButtonText={
          state.hasApplyButton
            ? 'Apply'
            : state.hasSelectVersionButton
            ? rewardOffer?.reward.type === RewardOfferType.WebPlan
              ? 'Select Subscription Option'
              : 'Select Version'
            : 'Purchase'
        }
        isSubscriptionExpired={state.isExpired}
        subscriptionExpiredAt={state.expiredAt}
        onClickLearnMore={
          state.hasLearnMoreButton ? onClickLearnMore : undefined
        }
        onClickShare={undefined} // {state.hasShareButton ? onClickShare : undefined}
        onClickPurchase={
          state.hasPurchaseButton
            ? state.hasSelectVersionButton
              ? onClickSelectVersion
              : onClickPurchase
            : undefined
        }
        onClickEnterClub={
          state.purchasedButtonDisabled &&
          state.enterPurchasedClubButton !== undefined
            ? onClickEnterPurchasedClub
            : undefined
        }
        additionalDetails={additionalDetails}
      />
    </>
  );
};

export default WebStoreProductDetailScreen;
